import type { IntlShape } from 'react-intl';

import type { CreateFlagArgs } from '@atlaskit/flag';
import type { UploadRejectionData } from '@atlaskit/media-picker';

import { messages } from './messages';

// Constants
const SIZE_LIMIT_LEARN_MORE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/upload-a-file/';

const BYTES_PER_UNIT = 1024;
const DECIMAL_PLACES = 2;
const SIZE_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] as const;

/**
 * Calculates the appropriate unit index for a given byte size
 */
const calculateUnitIndex = (bytes: number, maxMagnitude?: number): number => {
	const calculatedIndex = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT));

	// Only apply maxMagnitude limit if it's truthy and less than the calculated index
	if (maxMagnitude && maxMagnitude < SIZE_UNITS.length - 1) {
		return Math.min(calculatedIndex, maxMagnitude);
	}

	return calculatedIndex;
};

/**
 * Formats bytes into a human-readable string with appropriate units
 */
const formatBytesToString = (bytes: number, unitIndex: number): string => {
	const convertedSize = bytes / BYTES_PER_UNIT ** unitIndex;
	const formattedSize = parseFloat(convertedSize.toFixed(DECIMAL_PLACES));
	return `${formattedSize} ${SIZE_UNITS[unitIndex]}`;
};

/**
 * Convert size limit bytes using base-2 conversion
 * @param bytes The number of bytes to convert
 * @param maxMagnitude The magnitude of the maximum unit desired (1 = KB, 2 = MB, etc.). 0 or undefined means no limit.
 * @returns Formatted string with size and unit
 */
export const getSizeLimitString = (bytes: number, maxMagnitude?: number): string => {
	if (bytes <= 0) {
		return '0 bytes';
	}

	// Validate maxMagnitude parameter (0 means no limit, so it's valid)
	if (maxMagnitude !== undefined && (maxMagnitude < 0 || maxMagnitude >= SIZE_UNITS.length)) {
		throw new Error(`maxMagnitude must be between 0 and ${SIZE_UNITS.length - 1}`);
	}

	const unitIndex = calculateUnitIndex(bytes, maxMagnitude);
	return formatBytesToString(bytes, unitIndex);
};

/**
 * Creates a flag configuration for file size limit exceeded error
 */
const createFileSizeLimitExceededFlag = (
	fileName: string,
	limit: number,
	intl: IntlShape,
): CreateFlagArgs => ({
	title: intl.formatMessage(messages.errorTitle, {
		fileName,
	}),
	description: intl.formatMessage(messages.errorDescription, {
		size: getSizeLimitString(limit),
	}),
	actions: [
		{
			content: intl.formatMessage(messages.learnMore),
			onClick: () => {
				window.open(SIZE_LIMIT_LEARN_MORE_LINK, '_blank');
			},
		},
	],
});

/**
 * Creates a flag configuration for empty file error
 */
const createEmptyFileErrorFlag = (data: UploadRejectionData, intl: IntlShape): CreateFlagArgs => ({
	title: intl.formatMessage(messages.zeroByteErrorTitle, {
		fileName: data.fileName,
	}),
	description: intl.formatMessage(messages.zeroByteErrorDescription),
});

/**
 * Creates appropriate flag configuration based on upload rejection reason
 * @param data Upload rejection data containing reason and file information
 * @param intl Internationalization shape for formatting messages
 * @returns Flag configuration object
 */
export const getAttachmentSizeLimitErrorFlag = (
	data: UploadRejectionData,
	intl: IntlShape,
): CreateFlagArgs => {
	switch (data.reason) {
		case 'fileSizeLimitExceeded':
			return createFileSizeLimitExceededFlag(data.fileName, data.limit, intl);
		default:
			return createEmptyFileErrorFlag(data, intl);
	}
};
